import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Container } from "./Container";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { full_name: "", company: "", email: "", message: "", botcheck: "" };
const PLANS = ["Контент", "Персонален", "Агенция"];

const labelCls = "block text-[11px] uppercase text-[var(--faint)] mb-[10px]";
const fieldCls =
  "w-full bg-transparent border-0 border-b border-[rgba(255,255,255,.22)] rounded-none pt-2 pb-3 text-[16px] text-[var(--white)] placeholder:text-[rgba(255,255,255,.30)] focus:outline-none focus:ring-0 focus:border-[var(--red)] transition-colors";

export const Contact = ({ interest = PLANS[0], onInterestChange }) => {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Моля, попълни име, имейл и съобщение.");
      return;
    }
    setLoading(true);
    // 1) първо запис в базата — никое запитване не се губи
    try {
      await axios.post(`${API}/inquiries`, {
        full_name: form.full_name,
        email: form.email,
        phone: form.company,
        message: `[Интересува ме: ${interest}] ${form.message}`,
      });
    } catch (err) {
      toast.error("Възникна грешка. Опитай отново.");
      setLoading(false);
      return;
    }
    // 2) имейл известие към bulbit.adv@gmail.com чрез Web3Forms
    try {
      const res = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          access_key: process.env.REACT_APP_WEB3FORMS_ACCESS_KEY,
          subject: `Ново запитване от ${form.full_name}`,
          from_name: "Bulb It — сайт",
          replyto: form.email,
          botcheck: form.botcheck,
          "Име": form.full_name,
          "Компания": form.company || "—",
          "Имейл": form.email,
          "Интересува ме": interest,
          "Съобщение": form.message,
        }),
      });
      const result = await res.json().catch(() => ({ success: false }));
      if (!res.ok || result.success === false) throw new Error(result.message || "web3forms");
    } catch (err) {
      // записът е в базата; само имейлът не е изпратен — отбелязваме за ръчна проверка
      console.error("Web3Forms delivery failed:", err);
    }
    setSent(true);
    setLoading(false);
  };

  return (
    <section id="contact" className="relative min-h-[100svh] flex items-center py-24 bg-[var(--dark)]" data-testid="contact-section">
      <Container className="grid grid-cols-1 lg:grid-cols-2 gap-[90px] items-start" data-testid="contact-grid">
        {/* left */}
        <div>
          <p className="text-[11px] uppercase text-[var(--red)] mb-5" style={{ letterSpacing: ".2em" }}>Контакти</p>
          <h2 className="font-display uppercase text-4xl lg:text-[42px] text-[var(--white)]" data-testid="contact-headline">
            ХАЙДЕ ДА<br /><span className="text-[var(--red)]">ЗАПОЧВАМЕ</span>
          </h2>
          <p className="mt-8 max-w-[38ch] text-[17px] text-white" data-testid="contact-intro">
            Разкажи ни накратко по какво работиш. Връщаме се с отговор до един работен ден.
          </p>
          <div className="mt-[52px] flex flex-wrap gap-[44px]" data-testid="contact-facts">
            {[["ОТГОВОР ДО", "24 часа"], ["РАБОТИМ ОТ", "2016 г."], ["БАЗА", "София"]].map(([l, v]) => (
              <div key={l}>
                <p className="text-[11px] uppercase text-[var(--faint)]" style={{ letterSpacing: ".14em" }}>{l}</p>
                <p className="text-[15px] text-[var(--white)] mt-1">{v}</p>
              </div>
            ))}
          </div>
        </div>

        {/* right — form */}
        <div>
          {sent ? (
            <p className="text-2xl text-[var(--white)]" data-testid="contact-success">Благодарим! Ще се чуем до един работен ден.</p>
          ) : (
            <form onSubmit={submit} className="space-y-8" data-testid="contact-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                <div>
                  <label className={labelCls}>Име</label>
                  <input className={fieldCls} value={form.full_name} onChange={upd("full_name")} data-testid="contact-input-name" />
                </div>
                <div>
                  <label className={labelCls}>Компания</label>
                  <input className={fieldCls} value={form.company} onChange={upd("company")} data-testid="contact-input-company" />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                <div>
                  <label className={labelCls}>Имейл</label>
                  <input type="email" className={fieldCls} value={form.email} onChange={upd("email")} data-testid="contact-input-email" />
                </div>
                <div>
                  <label className={labelCls}>Интересува ме</label>
                  <select className={`${fieldCls} appearance-none`} value={interest} onChange={(e) => onInterestChange?.(e.target.value)} data-testid="contact-input-interest">
                    {PLANS.map((p) => (
                      <option key={p} className="text-black" value={p}>{p}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label className={labelCls}>Какво ти трябва?</label>
                <textarea className={`${fieldCls} h-[86px] resize-none`} value={form.message} onChange={upd("message")} data-testid="contact-input-message" />
              </div>
              {/* honeypot — невидим за хора, спира прости ботове (Web3Forms botcheck) */}
              <input
                type="text"
                tabIndex="-1"
                autoComplete="off"
                aria-hidden="true"
                className="hidden"
                value={form.botcheck}
                onChange={upd("botcheck")}
                data-testid="contact-botcheck"
              />
              <button type="submit" disabled={loading} className="rounded-full bg-[var(--red)] text-white px-8 py-3.5 text-[15px] font-semibold uppercase tracking-wide disabled:opacity-60" data-testid="contact-submit">
                {loading ? "Изпращане…" : "Изпрати запитване"}
              </button>
            </form>
          )}
        </div>
      </Container>
    </section>
  );
};

export default Contact;
